//#region src/identificadores/modulo11.ts
/**
* Valor de um caractere no cálculo do DV: ASCII − 48 (NT Conjunta 2025.001). Para dígitos dá o
* próprio número; letras maiúsculas valem de 17 (A) a 42 (Z) — é o que viabiliza o CNPJ
* alfanumérico sem mudar o algoritmo.
*/
function valorDoCaractere(caractere) {
	return caractere.charCodeAt(0) - 48;
}
/** Soma ponderada pelos pesos indicados, aplicados da esquerda para a direita. */
function somaPonderada(texto, pesos) {
	return pesos.reduce((soma, peso, i) => soma + valorDoCaractere(texto.charAt(i)) * peso, 0);
}
/** Regra comum do módulo 11 dos documentos fiscais: resto < 2 → 0; senão 11 − resto. */
function digitoModulo11(soma) {
	const resto = soma % 11;
	return resto < 2 ? 0 : 11 - resto;
}
//#endregion
//#region src/identificadores/chave.ts
/** `[0-9]{6}[A-Z0-9]{12}[0-9]{26}` — letras só na raiz/ordem do CNPJ (NT Conjunta 2025.001). */
const FORMATO$1 = /^[0-9]{6}[0-9A-Z]{12}[0-9]{26}$/;
const MODELOS_CHAVE = {
	"55": "NFE_55",
	"65": "NFCE_65",
	"57": "CTE_57",
	"67": "CTE_OS_67",
	"58": "MDFE_58"
};
/** Tira espaços e máscara que planilhas e DANFEs costumam trazer (blocos de 4). */
function normalizarChave(valor) {
	return valor.replace(/[^0-9a-zA-Z]/g, "").toUpperCase();
}
/** DV da chave: pesos 2..9 da direita para a esquerda sobre as 43 primeiras posições. */
function calcularDigitoChave(chave43) {
	let soma = 0;
	for (let i = 0; i < chave43.length; i++) {
		const caractere = chave43.charAt(chave43.length - 1 - i);
		soma += valorDoCaractere(caractere) * (2 + i % 8);
	}
	return digitoModulo11(soma);
}
function chaveValida(valor) {
	const chave = normalizarChave(valor);
	if (!FORMATO$1.test(chave)) return false;
	return calcularDigitoChave(chave.slice(0, 43)) === Number(chave.charAt(43));
}
/** Decompõe uma chave válida; devolve `null` se formato ou DV não conferirem. */
function decomporChave(valor) {
	const chave = normalizarChave(valor);
	if (!chaveValida(chave)) return null;
	const codigoModelo = chave.slice(20, 22);
	return {
		chave,
		codigoUf: chave.slice(0, 2),
		anoMes: `20${chave.slice(2, 4)}-${chave.slice(4, 6)}`,
		documentoEmitente: chave.slice(6, 20),
		modelo: codigoModelo in MODELOS_CHAVE ? MODELOS_CHAVE[codigoModelo] : null,
		codigoModelo,
		serie: chave.slice(22, 25),
		numero: chave.slice(25, 34),
		tipoEmissao: chave.slice(34, 35),
		codigoNumerico: chave.slice(35, 43)
	};
}
/** Procura chaves de acesso válidas num texto livre (célula de planilha, nome de arquivo). */
function extrairChaves(texto) {
	const candidatos = texto.toUpperCase().match(/[0-9A-Z][0-9A-Z\s.-]{42,60}/g) ?? [];
	const encontradas = /* @__PURE__ */ new Set();
	for (const candidato of candidatos) {
		const compacto = normalizarChave(candidato);
		for (let i = 0; i + 44 <= compacto.length; i++) {
			const trecho = compacto.slice(i, i + 44);
			if (chaveValida(trecho)) encontradas.add(trecho);
		}
	}
	return [...encontradas];
}
//#endregion
//#region src/identificadores/cnpj.ts
const PESOS_DV1 = [
	5,
	4,
	3,
	2,
	9,
	8,
	7,
	6,
	5,
	4,
	3,
	2
];
const PESOS_DV2 = [6, ...PESOS_DV1];
/** 12 posições alfanuméricas (raiz + ordem) e 2 de DV sempre numéricas (IN RFB 2.229/2024). */
const FORMATO = /^[0-9A-Z]{12}[0-9]{2}$/;
/** Remove máscara e espaços e põe em maiúsculas: "12.abc.345/01de-35" → "12ABC34501DE35". */
function normalizarCnpj(valor) {
	return valor.replace(/[^0-9a-zA-Z]/g, "").toUpperCase();
}
/** Valida CNPJ numérico ou alfanumérico (já normalizado ou com máscara). */
function cnpjValido(valor) {
	const cnpj = normalizarCnpj(valor);
	if (!FORMATO.test(cnpj) || /^(\d)\1{13}$/.test(cnpj)) return false;
	const base = cnpj.slice(0, 12);
	const dv1 = digitoModulo11(somaPonderada(base, PESOS_DV1));
	const dv2 = digitoModulo11(somaPonderada(`${base}${dv1}`, PESOS_DV2));
	return cnpj.endsWith(`${dv1}${dv2}`);
}
/** Raiz do CNPJ (8 primeiras posições): agrupa matriz e filiais. */
function raizDoCnpj(valor) {
	return normalizarCnpj(valor).slice(0, 8);
}
/** "12ABC34501DE35" → "12.ABC.345/01DE-35". Devolve o valor normalizado se não tiver 14 posições. */
function formatarCnpj(valor) {
	const c = normalizarCnpj(valor);
	if (c.length !== 14) return c;
	return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
}
//#endregion
export { MODELOS_CHAVE as a, decomporChave as c, digitoModulo11 as d, somaPonderada as f, raizDoCnpj as i, extrairChaves as l, formatarCnpj as n, calcularDigitoChave as o, normalizarCnpj as r, chaveValida as s, cnpjValido as t, normalizarChave as u };

//# sourceMappingURL=cnpj-CT6DIYvC.mjs.map